import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {  clearError } from "../../redux/therapistSlice";
import { getBranches } from "../../redux/branchSlice";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useNavigate } from "react-router-dom";
import { 
  Checkbox, 
  FormControlLabel, 
  FormGroup,
  CircularProgress,
  IconButton
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { toast } from "react-toastify";
import axios from "axios"; // Direct request with axios

import PolicyModal from "../../common/Policy/PolicyModal";

const API_URL = process.env.REACT_APP_API_URL;

const SignUpTherapistForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // Get data from therapist slice
  const therapistState = useSelector((state) => state.therapists) || { 
    loading: false, 
    error: null,
    verificationSent: false
  };
  
  const { 
    loading, 
    error, 
    verificationSent
  } = therapistState;
  
  // Get data from branch slice
  const branchState = useSelector((state) => state.branches) || {
    branches: [],
    loading: false,
    error: null
  };
  
  const {
    branches = [],
    loading: branchesLoading = false,
    error: branchesError = null
  } = branchState;

  const [formData, setFormData] = useState({
    name: "",
    surname: "",
    email: "",
    phoneNumber: "",
    countryCode: "+90",
    branchId: "", // Branch field
    password: "",
    passwordConfirm: "",
    resume: null, // CV upload field
    policy1: false,
    policy2: false,
    policy3: false,
  });

  // Password visibility toggles
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  const [fileSelected, setFileSelected] = useState(false);
  const [fileName, setFileName] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [directLoading, setDirectLoading] = useState(false);

  const therapistAgreementContent = t("therapistAgreementContent", { returnObjects: true });
  const explicitConsentContent = t("explicitConsentFormContent", { returnObjects: true });
  const commercialConsentContent = t("commercialElectronicMessageConsentFormContent", { returnObjects: true });

  const renderPolicyBlocks = (blocks) => {
    if (!Array.isArray(blocks)) {
      return <p className="text-gray-700">{blocks}</p>;
    }

    return blocks.map((block, index) => {
      if (block.type === "heading") {
        const HeadingTag = block.level === 4 ? "h4" : block.level === 3 ? "h3" : "h2";
        const headingBase = "font-semibold text-primary";
        const headingSizing =
          block.level === 2
            ? "text-2xl"
            : block.level === 3
            ? "text-xl"
            : "text-lg";
        const isPrimaryHeading = index === 0;
        const headingClasses = [
          isPrimaryHeading ? "mt-0" : "mt-6",
          headingBase,
          isPrimaryHeading ? "text-3xl text-center" : headingSizing
        ].join(" ");
        return (
          <HeadingTag key={`heading-${index}`} className={headingClasses}>
            {block.content}
          </HeadingTag>
        );
      }

      if (block.type === "paragraph") {
        return (
          <p key={`paragraph-${index}`} className="mt-2 text-gray-700">
            {block.content}
          </p>
        );
      }

      if (block.type === "list") {
        return (
          <ul
            key={`list-${index}`}
            className="mt-2 list-disc space-y-1 pl-6 text-gray-700 marker:text-primary"
          >
            {block.items?.map((item, itemIndex) => (
              <li key={`list-${index}-${itemIndex}`}>{item}</li>
            ))}
          </ul>
        );
      }

      return null;
    });
  };

  // Policy modal state
  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const [policyType, setPolicyType] = useState("");
  const handlePolicyModalOpen = (type) => {
    setPolicyType(type);
    setPolicyModalOpen(true);
  };
  const handlePolicyModalClose = () => setPolicyModalOpen(false);

  // Load branches
  useEffect(() => {
    dispatch(getBranches());
  }, [dispatch]);

  // Check branch error status
  useEffect(() => {
    if (branchesError) {
      toast.error(`An error occurred while loading branches: ${branchesError}`, {
        autoClose: 8000, // 8 seconds
      });
    }
  }, [branchesError]);

  // Check therapist error status
  useEffect(() => {
    if (error) {
      toast.error(error, {
        autoClose: 8000, // 8 seconds
      });
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 8500); // Clear error slightly after toast disappears
      
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  // Redirect when verification email is sent
  useEffect(() => {
    if (verificationSent) {
      toast.success("Registration successful! A verification link has been sent to your email address.", {
        autoClose: 10000, // 10 seconds for success messages
      });
      navigate("/signInTherapist", { 
        state: { 
          email: formData.email,
          message: "We've sent a verification link to your email address. Please check your email.",
          status: "pending",
          userType: "therapist" 
        } 
      });
    }
  }, [verificationSent, formData.email, navigate]);

  const validateForm = () => {
    const errors = {};
    
    // Name validation
    if (formData.name.length < 2) {
      errors.name = "Name must be at least 2 characters long.";
    }
    
    // Surname validation
    if (formData.surname.length < 2) {
      errors.surname = "Surname must be at least 2 characters long.";
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      errors.email = "Please enter a valid email address.";
    }
    
    // Phone validation
    if (formData.phoneNumber.length < 10) {
      errors.phoneNumber = "Please enter a valid phone number.";
    }
    
    // Branch validation
    if (!formData.branchId) {
      errors.branchId = "Please select a branch.";
    }
    
    // Password validation - Güncellendi: Daha esnek validation
    if (formData.password.length < 8) {
      errors.password = "Password must be at least 8 characters long.";
    }
    
    // Password match validation
    if (formData.password !== formData.passwordConfirm) {
      errors.passwordConfirm = "Passwords do not match.";
    }
    
    // CV validation
    if (!formData.resume) {
      errors.resume = "Resume upload is required.";
    }
    
    // Policy approval
    if (!formData.policy1 || !formData.policy2 || !formData.policy3) {
      errors.policy = "You must accept all agreements.";
    }
    
    return errors;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    
    if (file) {
      // File type validation
      if (file.type !== "application/pdf") {
        setValidationErrors({
          ...validationErrors,
          resume: "You can only upload PDF files."
        });
        return;
      }
      
      // File size validation (max 50MB - S3 upload limit ile uyumlu)
      if (file.size > 50 * 1024 * 1024) {
        setValidationErrors({
          ...validationErrors,
          resume: "File size cannot exceed 50MB."
        });
        return;
      }
      
      setFormData({ ...formData, resume: file });
      setFileSelected(true);
      setFileName(file.name);
      setValidationErrors({
        ...validationErrors,
        resume: null
      });
    }
  };

  const handleChange = (e) => {
    const { name, checked, value, type } = e.target;
    
    // For checkbox values
    if (type === "checkbox") {
      setFormData({
        ...formData,
        [name]: checked
      });
      
      // Clear policy error if all checkboxes are checked
      if (validationErrors.policy && name.startsWith('policy')) {
        if (
          (name === 'policy1' && checked && formData.policy2 && formData.policy3) ||
          (name === 'policy2' && checked && formData.policy1 && formData.policy3) ||
          (name === 'policy3' && checked && formData.policy1 && formData.policy2)
        ) {
          setValidationErrors({
            ...validationErrors,
            policy: null
          });
        }
      }
      
      return;
    }
    
    // For normal input fields
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error for the related field
    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: null
      });
    }
  };

  // Direct axios submission - S3 upload compatible
  const handleDirectSubmit = async (e) => {
    e.preventDefault();
    
    // Form validation
    const errors = validateForm();
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      
      // Show toast for first error
      const firstError = Object.values(errors)[0];
      toast.error(firstError, {
        autoClose: 8000, // 8 seconds for validation errors
      });
      
      return;
    }
    
    try {
      setDirectLoading(true);
      
      const toTitleCase = (str) =>
        str.trim().split(/\s+/).map(word =>
          word.charAt(0).toLocaleUpperCase("tr") + word.slice(1).toLowerCase()
        ).join(" ");

      const formDataToSend = new FormData();

      // Basic information
      formDataToSend.append("name", toTitleCase(formData.name));
      formDataToSend.append("surname", formData.surname.trim());
      formDataToSend.append("email", formData.email);
      formDataToSend.append("password", formData.password);
      
      // Contact and phone information - Backend ile uyumlu format
      formDataToSend.append("contact[countryCode]", formData.countryCode);
      formDataToSend.append("contact[phoneNumber]", formData.phoneNumber);
      
      // Branch ID
      formDataToSend.append("branchId", formData.branchId);
      
      // File - Orijinal dosya adıyla gönder (backend'de timestamp eklenecek)
      formDataToSend.append("resume", formData.resume);
      
      // Policy information - Backend boolean değerleri bekliyor
      formDataToSend.append("memberAg", formData.policy1);
      formDataToSend.append("infoText", formData.policy2);
      formDataToSend.append("perData", formData.policy3);
      
      // Debug form content
      console.log("Sending form data:");
      for (let [key, value] of formDataToSend.entries()) {
        console.log(`${key}:`, value);
      }
      
      // Direct API call
      const response = await axios.post(`${API_URL}/therapists/signup`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        // Upload progress tracking (opsiyonel)
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload Progress: ${percentCompleted}%`);
        }
      });
      
      console.log("Registration response:", response.data);
      
      toast.success("Registration successful! A verification link has been sent to your email address.", {
        autoClose: 10000,
      });
      
      // S3 URL'i konsola yazdır (isteğe bağlı)
      if (response.data.resumeUrl) {
        console.log("Resume uploaded to:", response.data.resumeUrl);
      }
      
      // Redirect to sign in page
      navigate("/signInTherapist", { 
        state: { 
          email: formData.email,
          message: "We've sent a verification link to your email address. Please check your email.",
          status: "pending",
          userType: "therapist" 
        } 
      });
      
    } catch (error) {
      console.error("Registration error:", error.response?.data || error);
      
      // Daha detaylı hata mesajları
      let errorMessage = "An error occurred during registration. Please try again.";
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.code === 'NETWORK_ERROR') {
        errorMessage = "Network error. Please check your internet connection.";
      } else if (error.response?.status === 413) {
        errorMessage = "File size is too large. Please choose a smaller file.";
      }
      
      toast.error(errorMessage, {
        autoClose: 10000, 
      });
    } finally {
      setDirectLoading(false);
    }
  };

  return (
    <div className="mt-[22px]">
      <p className="text-text font-poppins text-[20px] ml-4 mb-4">
        {t("therapistregistration")}
      </p>

      <div className="overflow-hidden">
        <form onSubmit={handleDirectSubmit} encType="multipart/form-data">
          {/* Error Message */}
          {error && (
            <div className="text-red-500 bg-red-100 p-3 rounded-md mb-4">
              {error}
            </div>
          )}

          {/* Name */}
          <div className="mb-2">
            <input
              type="text"
              placeholder={t("name")}
              className={`w-full px-4 py-2 border-2 ${
                validationErrors.name ? "border-red-500" : "border-cards-border"
              } focus:ring-2 focus:ring-[#1B5E20] rounded-tr-[30px] rounded-tl-[30px]`}
              name="name"
              value={formData.name}
              onChange={handleChange}
            />
            {validationErrors.name && (
              <p className="text-red-500 text-xs mt-1 ml-2">{validationErrors.name}</p>
            )}
          </div>

          {/* Surname */}
          <div className="mb-2">
            <input
              type="text"
              placeholder={t("surname")}
              className={`w-full px-4 py-2 border-2 ${
                validationErrors.surname ? "border-red-500" : "border-cards-border"
              } focus:ring-2 focus:ring-[#1B5E20]`}
              name="surname"
              value={formData.surname}
              onChange={handleChange}
            />
            {validationErrors.surname && (
              <p className="text-red-500 text-xs mt-1 ml-2">{validationErrors.surname}</p>
            )}
          </div>

          {/* Email */}
          <div className="mb-2">
            <input
              type="email"
              placeholder="E-mail"
              className={`w-full px-4 py-2 border-2 ${
                validationErrors.email ? "border-red-500" : "border-cards-border"
              } focus:ring-2 focus:ring-[#1B5E20]`}
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
            {validationErrors.email && (
              <p className="text-red-500 text-xs mt-1 ml-2">{validationErrors.email}</p>
            )}
          </div>

          {/* Phone */}
          <div className="mb-2">
            <div className={`flex items-center border-2 ${
              validationErrors.phoneNumber ? "border-red-500" : "border-cards-border"
            }`}>
              <select
                className="px-4 py-2 text-[16px] text-text font-poppins focus:ring-2 focus:ring-[#1B5E20] border-r-2 border-cards-border"
                value={formData.countryCode}
                onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                name="countryCode"
              >
                <option value="+90">+90</option>
                <option value="+1">+1</option>
                <option value="+44">+44</option>
                <option value="+49">+49</option>
              </select>

              <input
                type="tel"
                placeholder={t("phone")}
                pattern="[0-9]*"
                inputMode="numeric"
                className="w-full px-4 py-2 bg-white text-[16px] focus:outline-none focus:ring-2 focus:ring-[#1B5E20]"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={(e) =>
                  setFormData({ ...formData, phoneNumber: e.target.value.replace(/\D/g, '').slice(0, 10) })
                }
                required
              />
            </div>
            {validationErrors.phoneNumber && (
              <p className="text-red-500 text-xs mt-1 ml-2">{validationErrors.phoneNumber}</p>
            )}
          </div>

          {/* Branch Selection */}
          <div className="mb-2">
            <div className="relative">
              <select
                name="branchId"
                value={formData.branchId}
                onChange={handleChange}
                className={`w-full px-4 py-2 border-2 appearance-none ${
                  validationErrors.branchId ? "border-red-500" : "border-cards-border"
                } focus:ring-2 focus:ring-[#1B5E20] bg-white`}
              >
                <option value="" disabled>{t("branch")}</option>
                {branchesLoading ? (
                  <option value="" disabled>Loading branches...</option>
                ) : branches && branches.length > 0 ? (
                  branches.map((branch) => (
                    <option key={branch._id} value={branch._id}>
                      {branch.name}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>No branches found</option>
                )}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            {validationErrors.branchId && (
              <p className="text-red-500 text-xs mt-1 ml-2">{validationErrors.branchId}</p>
            )}
          </div>

          {/* Password */}
          <div className="mb-2 relative">
            <input
              type={showPassword ? "text" : "password"}
              placeholder={t("password")}
              className={`w-full px-4 py-2 border-2 ${
                validationErrors.password ? "border-red-500" : "border-cards-border"
              } focus:ring-2 focus:ring-[#1B5E20]`}
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
            />
            <IconButton
              onClick={() => setShowPassword((prev) => !prev)}
              edge="end"
              sx={{
                position: "absolute",
                right: "36px",
                mt: "20px",
                transform: "translateY(-50%)",
              }}
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
            {validationErrors.password ? (
              <p className="text-red-500 text-xs mt-1 ml-2">{validationErrors.password}</p>
            ) : (
              <p className="text-gray-500 text-xs mt-1 ml-2">
                {t("passwordInfo")}
              </p>
            )}
          </div>

          {/* Password Confirm */}
          <div className="mb-4 relative">
            <input
              type={showPasswordConfirm ? "text" : "password"}
              placeholder={t("passwordConfirm")}
              className={`w-full px-4 py-2 border-2 ${
                validationErrors.passwordConfirm ? "border-red-500" : "border-cards-border"
              } focus:ring-2 focus:ring-[#1B5E20] rounded-br-[30px] rounded-bl-[30px]`}
              name="passwordConfirm"
              value={formData.passwordConfirm}
              onChange={handleChange}
              required
            />
            <IconButton
              onClick={() => setShowPasswordConfirm((prev) => !prev)}
              edge="end"
              sx={{
                position: "absolute",
                right: "36px",
                top: "50%",
                transform: "translateY(-50%)",
              }}
            >
              {showPasswordConfirm ? <VisibilityOff /> : <Visibility />}
            </IconButton>
            {validationErrors.passwordConfirm && (
              <p className="text-red-500 text-xs mt-1 ml-2">{validationErrors.passwordConfirm}</p>
            )}
          </div>

          {/* CV Upload Area - S3 Upload Compatible */}
          <div className={`mt-8 p-4 border-2 ${
            validationErrors.resume ? "border-red-500" : "border-cards-border"
          } rounded-lg bg-gray-50`}>
            <p className="text-[18px] font-poppins text-[#454545] mb-2 flex items-center">
              <CloudUploadIcon className="mr-2" />
              {t("uploadCV")}
            </p>
            
            <div className="relative">
              <input
                type="file"
                accept=".pdf"
                id="resume-upload"
                name="resume"
                className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                onChange={handleFileChange}
              />
              <div className={`w-full h-[60px] border-2 ${
                validationErrors.resume ? "border-red-500" : "border-dashed border-cards-border"
              } rounded-lg flex items-center justify-center cursor-pointer hover:bg-green-50 transition-colors`}>
                {fileSelected ? (
                  <div className="flex items-center text-green-700">
                    <CloudUploadIcon className="mr-2" />
                    <p className="truncate max-w-md px-3">{fileName}</p>
                  </div>
                ) : (
                  <p className="text-gray-600">{t("fileClick")}</p>
                )}
              </div>
            </div>
            
            {validationErrors.resume ? (
              <p className="text-red-500 text-xs mt-2">{validationErrors.resume}</p>
            ) : (
              <p className="text-gray-500 text-xs mt-2">
                Only PDF files allowed. Maximum file size: 50MB.
              </p>
            )}
          </div>

          {/* Agreement Checkboxes */}
          <FormGroup
            sx={{
              p: 3,
              gap: 3,
            }}
            className={`${validationErrors.policy ? "border-l-4 border-red-500 pl-2" : ""} rounded-2xl border border-[#625480]/15 bg-white/60 shadow-sm`}
          >
            <FormControlLabel
              control={
                <Checkbox
                  required
                  checked={formData.policy1}
                  onChange={handleChange}
                  name="policy1"
                  sx={{
                    '&.MuiCheckbox-root': {
                      borderRadius: '6px',
                      width: '24px',
                      height: '24px',
                    },
                    color: '#625480',
                    '&.Mui-checked': {
                      color: '#625480',
                    }
                  }}
                />
              }
              label={
                <span className="flex flex-col gap-2 text-left">
                  <span className="text-[18px] font-medium leading-relaxed text-[#1F1F1F]">
                    {t("policyTherapist-1")}
                  </span>
                  <button
                    type="button"
                    onClick={() => handlePolicyModalOpen("policy1")}
                    className="self-start rounded-full border border-[#625480]/40 px-3 py-1 text-sm font-medium text-[#625480] transition hover:bg-[#625480] hover:text-white"
                  >
                    {t("viewDocument")}
                  </button>
                </span>
              }
              sx={{
                width: '100%',
                alignItems: 'flex-start',
                '& .MuiFormControlLabel-label': {
                  marginLeft: '12px'
                },
                mb: 3,
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  required
                  checked={formData.policy2}
                  onChange={handleChange}
                  name="policy2"
                  sx={{
                    '&.MuiCheckbox-root': {
                      borderRadius: '6px',
                      width: '24px',
                      height: '24px',
                    },
                    color: '#625480',
                    '&.Mui-checked': {
                      color: '#625480',
                    }
                  }}
                />
              }
              label={
                <span className="flex flex-col gap-2 text-left">
                  <span className="text-[18px] font-medium leading-relaxed text-[#1F1F1F]">
                    {t("policyTherapist-2")}
                  </span>
                  <button
                    type="button"
                    onClick={() => handlePolicyModalOpen("policy2")}
                    className="self-start rounded-full border border-[#625480]/40 px-3 py-1 text-sm font-medium text-[#625480] transition hover:bg-[#625480] hover:text-white"
                  >
                    {t("viewDocument")}
                  </button>
                </span>
              }
              sx={{
                width: '100%',
                alignItems: 'flex-start',
                '& .MuiFormControlLabel-label': {
                  marginLeft: '12px'
                },
                mb: 3
              }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  required
                  checked={formData.policy3}
                  onChange={handleChange}
                  name="policy3"
                  sx={{
                    '&.MuiCheckbox-root': {
                      borderRadius: '6px',
                      width: '24px',
                      height: '24px',
                    },
                    color: '#625480',
                    '&.Mui-checked': {
                      color: '#625480',
                    }
                  }}
                />
              }
              label={
                <span className="flex flex-col gap-2 text-left">
                  <span className="text-[18px] font-medium leading-relaxed text-[#1F1F1F]">
                    {t("policy-3")}
                  </span>
                  <button
                    type="button"
                    onClick={() => handlePolicyModalOpen("policy3")}
                    className="self-start rounded-full border border-[#625480]/40 px-3 py-1 text-sm font-medium text-[#625480] transition hover:bg-[#625480] hover:text-white"
                  >
                    {t("viewDocument")}
                  </button>
                </span>
              }
              sx={{
                width: '100%',
                alignItems: 'flex-start',
                '& .MuiFormControlLabel-label': {
                  marginLeft: '12px'
                },
                mb: 0
              }}
            />

            {validationErrors.policy && (
              <p className="text-red-500 text-xs mt-1">{validationErrors.policy}</p>
            )}
          </FormGroup>

          {/* Register Button */}
          <button
            type="submit"
            className="w-full h-[61px] bg-button-bg text-white py-2 rounded-full text-[20px] font-poppins mt-6 flex justify-center items-center disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || directLoading}
          >
            {loading || directLoading ? (
              <>
                <CircularProgress size={24} color="inherit" className="mr-2" />
                Registering...
              </>
            ) : (
              t("signUp")
            )}
          </button>
          
          <PolicyModal
            isOpen={policyModalOpen}
            onClose={handlePolicyModalClose}
            title={
              policyType === "policy1"
                ? t("therapistAgreement")
                : policyType === "policy2"
                ? t("explicitConsentForm")
                : t("commercialElectronicMessageConsentForm")
            }
          >
            {policyType === "policy1" ? (
              <div className="space-y-4 text-sm leading-6 text-gray-700">
                {renderPolicyBlocks(therapistAgreementContent)}
              </div>
            ) : policyType === "policy2" ? (
              <div className="space-y-4 text-sm leading-6 text-gray-700">
                {renderPolicyBlocks(explicitConsentContent)}
              </div>
            ) : policyType === "policy3" ? (
              <div className="space-y-4 text-sm leading-6 text-gray-700">
                {renderPolicyBlocks(commercialConsentContent)}
              </div>
            ) : (
              null
            )}
          </PolicyModal>
        </form>
      </div>
    </div>
  );
};

export default SignUpTherapistForm;
